import React from 'react'
import { validate } from 'email-validator'
import Table from '@/components/table/Table'
import Row from '@/components/table/Row'
import PopupDialog from '@/components/popupDialog/PopupDialog'
import RoleDropDown from '@/components/roleDropDown/RoleDropDown'
import AddField from '@/components/addField/AddField'
import CellList from '@/components/table/CellList'
import {
  useLazyGetUsersQuery,
  useLazyAddUserQuery,
  useLazyDeleteUserQuery,
  useLazyUpdateUserQuery,
  useLazyResendInventationMailQuery,
  IUserCreate,
  IUserUpdate,
  IUserObject,
  ERole,
} from '@/store/apis/users'
import { useTranslation } from 'react-i18next'
import { parseISO } from 'date-fns'
import ResendButton from './ResendButton'

interface IState {
  selectedPopup?: 'ADD' | 'DELETE'
  deleteUser?: IUserObject
  addUser?: IUserCreate
  editUser?: IUserUpdate
}

const Users: React.FC = () => {
  const { t } = useTranslation()
  const [getUsersQuery, { data: usersData, isFetching: isGettingUsers }] =
    useLazyGetUsersQuery()
  const [addUserQuery, { isFetching: isAddingUser }] = useLazyAddUserQuery()
  const [deleteUserQuery, { isFetching: isDeletingUser }] =
    useLazyDeleteUserQuery()
  const [updateUserQuery, { isFetching: isUpdatingUser }] =
    useLazyUpdateUserQuery()
  const [resendInventationMailQuery, { isFetching: isResendingMail }] =
    useLazyResendInventationMailQuery()
  const [state, setState] = React.useState<IState>({})

  React.useEffect(() => {
    getUsersQuery({})
  }, [getUsersQuery])

  const handleAdd = async (): Promise<void> => {
    if (state.addUser) {
      await addUserQuery(state.addUser)
      await getUsersQuery({})
      setState({
        ...state,
        addUser: undefined,
        selectedPopup: undefined,
      })
    }
  }

  const handleDelete = async (id: number): Promise<void> => {
    await deleteUserQuery({ id })
    await getUsersQuery({})
    setState({
      ...state,
      selectedPopup: undefined,
      deleteUser: undefined,
    })
  }

  const handleEdit = async (id: number): Promise<void> => {
    if (state.editUser) {
      await updateUserQuery({ id, ...state.editUser })
      await getUsersQuery({})
      setState({ ...state, editUser: undefined })
    }
  }

  const { selectedPopup, deleteUser, addUser, editUser } = state

  const {
    items = [],
    // totalCount = 0,
    // limit = 10,
    // offset = 0,
    // sortKey = 'id',
    // sortDirection = 'ASC',
    // filter = {},
  } = usersData ? usersData : {}

  return (
    <>
      <Table
        headerLabels={[
          t('headers.firstName'),
          t('headers.sureName'),
          t('headers.email'),
          t('headers.role'),
          t('headers.verifiedResend'),
          t('headers.numberOfSchemas'),
        ]}
        isLoading={isGettingUsers}
        isEditing={!!editUser}
        isAddOpen={selectedPopup === 'ADD'}
        onAddOpen={() =>
          setState({
            ...state,
            selectedPopup: 'ADD',
            addUser: {
              firstName: '',
              lastName: '',
              email: '',
              phoneNumber: '',
              role: ERole.User,
            },
          })
        }
      >
        {items.map((user, index) => (
          <Row
            key={index}
            isLoading={isUpdatingUser}
            disableButtons={!!editUser}
            onEditOpen={() => setState({ ...state, editUser: user.user })}
            onEditSuccess={() => handleEdit(user.user.id)}
            onEditAbort={() => setState({ ...state, editUser: undefined })}
            onDeleteOpen={() =>
              setState({
                ...state,
                selectedPopup: 'DELETE',
                deleteUser: user.user,
              })
            }
            columns={{
              normal: [
                user.user.firstName,
                user.user.lastName,
                user.user.email,
                t(`roleType.${user.user.role.toLocaleLowerCase()}`),
                user.verified
                  ? t('yes')
                  : user.tokenTimeout && (
                      <ResendButton
                        disabled={isResendingMail || !!editUser}
                        validTo={parseISO(user.tokenTimeout)}
                        onClick={() => {
                          resendInventationMailQuery({ id: user.user.id })
                        }}
                      />
                    ),
                <CellList
                  value={user.schemas.length}
                  list={user.schemas.map((schema) => schema.name)}
                />,
              ],
              edit: editUser && [
                <AddField
                  value={editUser.firstName}
                  required={true}
                  regex={/^$/}
                  onChange={(newValue) =>
                    setState({
                      ...state,
                      editUser: { ...editUser, firstName: newValue },
                    })
                  }
                />,
                <AddField
                  value={editUser.lastName ? editUser.lastName : ''}
                  onChange={(newValue) =>
                    setState({
                      ...state,
                      editUser: { ...editUser, lastName: newValue },
                    })
                  }
                />,
                <AddField
                  value={editUser.email}
                  required={true}
                  error={!validate(editUser.email)}
                  onChange={(newValue) =>
                    setState({
                      ...state,
                      editUser: { ...editUser, email: newValue },
                    })
                  }
                />,
                <RoleDropDown
                  label=""
                  defaultValue={editUser.role}
                  onChange={(newValue) =>
                    setState({
                      ...state,
                      editUser: { ...editUser, role: newValue },
                    })
                  }
                />,
                user.verified
                  ? t('yes')
                  : user.tokenTimeout && (
                      <ResendButton
                        disabled={true}
                        validTo={parseISO(user.tokenTimeout)}
                        onClick={() => {}}
                      />
                    ),
                <CellList
                  value={user.schemas.length}
                  list={user.schemas.map((schema) => schema.name)}
                />,
              ],
            }}
          />
        ))}
        {selectedPopup === 'ADD' && addUser && (
          <Row
            isLoading={isAddingUser}
            onAddSuccess={() => handleAdd()}
            onAddAbort={() =>
              setState({
                ...state,
                addUser: undefined,
                selectedPopup: undefined,
              })
            }
            columns={{
              normal: [
                <AddField
                  value={addUser.firstName}
                  required={true}
                  regex={/^$/}
                  onChange={(newValue) =>
                    setState({
                      ...state,
                      addUser: { ...addUser, firstName: newValue },
                    })
                  }
                />,
                <AddField
                  value={addUser.lastName ? addUser.lastName : ''}
                  onChange={(newValue) =>
                    setState({
                      ...state,
                      addUser: { ...addUser, lastName: newValue },
                    })
                  }
                />,
                <AddField
                  value={addUser.email}
                  required={true}
                  error={!validate(addUser.email)}
                  onChange={(newValue) =>
                    setState({
                      ...state,
                      addUser: { ...addUser, email: newValue },
                    })
                  }
                />,
                <RoleDropDown
                  label=""
                  defaultValue={ERole.User}
                  onChange={(newValue) =>
                    setState({
                      ...state,
                      addUser: { ...addUser, role: newValue },
                    })
                  }
                />,
                '',
                '',
              ],
            }}
          />
        )}
      </Table>
      <PopupDialog
        isOpen={selectedPopup === 'DELETE'}
        title={
          <>
            {t('popups.removeUserTitle')}: <b>{deleteUser?.firstName}</b>
          </>
        }
        content={t('popups.removeUserDescription')}
        isLoading={isDeletingUser}
        onClose={() => setState({ ...state, selectedPopup: undefined })}
        onConfirm={() => (deleteUser?.id ? handleDelete(deleteUser.id) : null)}
      />
    </>
  )
}

export default Users
